import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import grievanceService from '../services/grievanceService';
import {
  MdOutlineReportProblem,
  MdFilterList,
  MdRefresh,
  MdPendingActions,
  MdCheckCircle,
  MdGroup,
  MdExpandMore,
  MdExpandLess,
  MdPerson,
  MdSend,
  MdWarning,
  MdTrendingUp,
  MdFormatListBulleted,
  MdInbox,
  MdChevronLeft,
  MdChevronRight,
  MdPhone
} from 'react-icons/md';

/* ── Atoms ── */

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { label: 'Pending',  cls: 'bg-status-warning-bg text-status-warning' },
    reviewed: { label: 'Reviewed', cls: 'bg-status-success-bg text-status-success' }
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
};

const ItemTag = ({ tag }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent-green/10 text-accent-green text-xs font-medium border border-accent-green/20">
    {tag}
  </span>
);

/* ── ConversationBox ── */

const ConversationBox = ({ grievanceId, conversation = [], legacyFeedback, onSaved }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const displayConversation = [...conversation];
  if (legacyFeedback && conversation.length === 0) {
    displayConversation.push({
      _id: 'legacy', senderRole: 'ADMIN', senderName: 'Admin',
      message: legacyFeedback, timestamp: new Date()
    });
  }

  const handleReply = async () => {
    const trimmed = replyMessage.trim();
    if (!trimmed) { toast.error('Message cannot be empty.'); return; }
    try {
      setSaving(true);
      const res = await grievanceService.addReply(grievanceId, trimmed);
      if (res.success) {
        toast.success('Reply sent!');
        setReplyMessage('');
        onSaved && onSaved(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reply.');
    } finally {
      setSaving(false);
    }
  };

  const hasMessages = displayConversation.length > 0;

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border-light pt-4">
      {hasMessages && (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
            <MdCheckCircle size={14} className="text-accent-green" /> Conversation
          </p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
            {displayConversation.map((msg, idx) => {
              const isMine = ['ADMIN', 'CHAIRMAN'].includes(msg.senderRole);
              return (
                <div key={msg._id || idx} className={`flex flex-col max-w-[85%] ${isMine ? 'ml-auto items-end' : 'mr-auto items-start'}`}>
                  <span className="text-[10px] text-text-tertiary mb-1 flex items-center gap-1">
                    <MdPerson size={10} /> {msg.senderName} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <div className={`p-3 rounded-2xl text-sm ${isMine ? 'bg-accent-green text-white rounded-tr-sm' : 'bg-background-secondary text-text-primary rounded-tl-sm border border-border-light'}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className={`mt-2 p-3 rounded-xl border border-border-light bg-surface shadow-sm focus-within:border-accent-green/50 focus-within:ring-1 focus-within:ring-accent-green/20 transition-all ${hasMessages ? '' : 'border-accent-green/30 bg-accent-green/5'}`}>
        {!hasMessages && (
          <p className="text-xs font-semibold text-accent-green uppercase tracking-wider mb-2">Add Feedback</p>
        )}
        <textarea
          rows={hasMessages ? 2 : 3}
          className="w-full px-1 py-1 bg-transparent focus:outline-none text-sm text-text-primary placeholder-text-tertiary resize-none"
          placeholder={hasMessages ? 'Type your reply…' : 'Write your feedback for this grievance…'}
          maxLength={3000}
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
        />
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-light/50">
          <span className="text-[10px] text-text-tertiary">{replyMessage.length}/3000</span>
          <button
            type="button"
            onClick={handleReply}
            disabled={saving || !replyMessage.trim()}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-accent-green text-white font-semibold hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {saving ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <MdSend size={12} />}
            {saving ? 'Sending…' : (hasMessages ? 'Send Reply' : 'Send Feedback')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── GrievanceCard ── */

const fmt = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

const DetailSection = ({ icon: Icon, label, children }) => (
  <div>
    <p className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
      <Icon size={13} className="text-accent-green" />{label}
    </p>
    {children}
  </div>
);

const GrievanceCard = ({ grievance, onUpdated }) => {
  const [open, setOpen] = useState(false);

  const handleSaved = (updated) => {
    onUpdated && onUpdated(updated);
  };

  return (
    <div className="bg-surface rounded-xl border border-border-light shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background transition-colors"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-text-primary line-clamp-1 max-w-xs">{grievance.subject}</span>
          <span className="text-xs text-text-tertiary">{fmt(grievance.createdAt)}</span>
          <span className="flex items-center gap-1 text-sm text-text-secondary">
            <MdPerson size={14} />{grievance.candidateId?.name || 'Candidate'}
          </span>
          {grievance.candidateId?.phoneNumber && (
            <span className="flex items-center gap-1 text-sm text-text-secondary">
              <MdPhone size={14} />{grievance.candidateId.phoneNumber}
            </span>
          )}
          <StatusBadge status={grievance.status} />
        </div>
        <span className="flex-shrink-0 text-text-tertiary ml-2">
          {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-border-light pt-4">
          <DetailSection icon={MdPerson} label="Candidate Info">
            <div className="flex flex-wrap gap-4 text-sm text-text-primary">
              <span className="flex items-center gap-1.5">
                <MdPerson size={14} className="text-accent-green" />
                {grievance.candidateId?.name || 'Unknown'}
              </span>
              {grievance.candidateId?.phoneNumber && (
                <span className="flex items-center gap-1.5">
                  <MdPhone size={14} className="text-accent-green" />
                  {grievance.candidateId.phoneNumber}
                </span>
              )}
            </div>
          </DetailSection>
          <DetailSection icon={MdOutlineReportProblem} label="Subject">
            <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{grievance.subject}</p>
          </DetailSection>
          {grievance.tags?.length > 0 && (
            <DetailSection icon={MdFormatListBulleted} label="Tags">
              <div className="flex flex-wrap gap-2">{grievance.tags.map((t, i) => <ItemTag key={i} tag={t} />)}</div>
            </DetailSection>
          )}
          {grievance.additionalDetails && (
            <DetailSection icon={MdWarning} label="Additional Details">
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{grievance.additionalDetails}</p>
            </DetailSection>
          )}
          {grievance.expectedResolution && (
            <DetailSection icon={MdTrendingUp} label="Expected Resolution">
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{grievance.expectedResolution}</p>
            </DetailSection>
          )}
          <ConversationBox
            grievanceId={grievance._id}
            conversation={grievance.conversation || []}
            legacyFeedback={grievance.adminFeedback}
            onSaved={handleSaved}
          />
        </div>
      )}
    </div>
  );
};

/* ── GrievanceList ── */

const GrievanceList = ({ grievances, pagination, onPageChange, loading, onUpdated }) => {
  const { page = 1, totalPages = 1, total = 0 } = pagination;

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-xl bg-background-secondary animate-pulse border border-border-light" />
        ))}
      </div>
    );
  }

  if (grievances.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-background-secondary flex items-center justify-center mb-4">
          <MdInbox size={28} className="text-text-tertiary" />
        </div>
        <p className="text-base font-semibold text-text-secondary">No grievances found</p>
        <p className="text-sm text-text-tertiary mt-1">No grievances match the selected filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-text-tertiary">Showing {grievances.length} of {total} grievance{total !== 1 ? 's' : ''}</p>
      <div className="space-y-3">
        {grievances.map((g) => (
          <GrievanceCard key={g._id} grievance={g} onUpdated={onUpdated} />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-text-secondary hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <MdChevronLeft size={16} /> Previous
          </button>
          <span className="text-sm text-text-secondary">Page <span className="font-semibold text-text-primary">{page}</span> of {totalPages}</span>
          <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-text-secondary hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Next <MdChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Page ── */

const StatCard = ({ icon: Icon, value, label, color, bg }) => (
  <div className={`rounded-xl p-4 ${bg} border border-transparent flex flex-col gap-1`}>
    <Icon size={20} className={color} />
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs font-medium text-text-secondary">{label}</p>
  </div>
);

const INPUT_CLS =
  'px-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-green/40 focus:border-accent-green transition-all duration-150';

const AdminGrievancesPage = () => {
  const [filters, setFilters] = useState({ status: '' });
  const [grievances, setGrievances] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0 });

  const fetchGrievances = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const res = await grievanceService.getAllGrievances({ ...filters, page, limit: 15 });
      if (res.success) {
        setGrievances(res.data.items);
        setPagination(res.data.pagination);
        setStats({
          total:    res.data.pagination.total,
          pending:  res.data.items.filter((g) => g.status === 'pending').length,
          reviewed: res.data.items.filter((g) => g.status === 'reviewed').length
        });
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchGrievances(currentPage); }, [fetchGrievances, currentPage]);

  const handleFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setCurrentPage(1);
  };

  const handleUpdated = (updated) => {
    setGrievances((prev) => prev.map((g) => (g._id === updated._id ? updated : g)));
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-green/10 flex items-center justify-center flex-shrink-0">
            <MdOutlineReportProblem size={22} className="text-accent-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight">Candidate Grievances</h1>
            <p className="text-sm text-text-secondary">Review and respond to grievances submitted by candidates.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={MdGroup}         value={stats.total}    label="Total Grievances"     color="text-accent-green"   bg="bg-accent-green/10" />
          <StatCard icon={MdPendingActions} value={stats.pending}  label="Pending (this page)"  color="text-status-warning" bg="bg-status-warning-bg" />
          <StatCard icon={MdCheckCircle}    value={stats.reviewed} label="Reviewed (this page)" color="text-status-success" bg="bg-status-success-bg" />
        </div>

        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-5">
          <div className="flex flex-wrap items-end gap-4">
            <MdFilterList size={18} className="text-text-tertiary flex-shrink-0 mb-2" />
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</label>
              <select
                className={INPUT_CLS}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
              </select>
            </div>
            <button
              onClick={() => { setFilters({ status: '' }); setCurrentPage(1); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm text-text-secondary hover:bg-background transition-colors self-end"
            >
              <MdRefresh size={16} /> Reset
            </button>
            <button
              onClick={() => fetchGrievances(currentPage)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-green text-white text-sm font-semibold hover:bg-accent-green-dark transition-colors self-end"
            >
              <MdRefresh size={16} /> Refresh
            </button>
          </div>
        </div>

        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-6">
          <GrievanceList
            grievances={grievances}
            pagination={pagination}
            onPageChange={(p) => setCurrentPage(p)}
            loading={loading}
            onUpdated={handleUpdated}
          />
        </div>

      </div>
    </MainLayout>
  );
};

export default AdminGrievancesPage;
